/**
 * Icons, drawn to the same rules as the brand mark: a 1.6 unit hairline on a
 * 24 unit grid, round caps, no fills. They sit next to the Blossom, so they
 * cannot look like they came from a different family.
 *
 * Every icon is presentational. The accessible name always lives on the
 * control that contains it, never on the glyph.
 */

interface IconProps {
  size?: number
  className?: string
}

function base(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  }
}

export function MicIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  )
}

export function MicOffIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M15 5a3 3 0 0 0-6 0v5" />
      <path d="M5 11a7 7 0 0 0 10.9 5.8" />
      <path d="M19 11a7 7 0 0 1-.4 2.3" />
      <path d="M12 18v3" />
      <path d="M4 3l16 18" />
    </svg>
  )
}

export function PauseIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M9 5v14" />
      <path d="M15 5v14" />
    </svg>
  )
}

export function PlayIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M8 5l11 7-11 7z" />
    </svg>
  )
}

export function StopIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  )
}

/**
 * Hanging up: the handset, turned down.
 *
 * The one icon here that is not drawn from first principles, and deliberately
 * so. Ending a call has a shape everybody already knows, and a control that
 * stops a conversation is the wrong place to ask someone to learn a new glyph.
 */
export function EndCallIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <g transform="rotate(135 12 12)">
        <path d="M8.4 4.2 5.6 5.1c-.9.3-1.4 1.3-1.1 2.2 1.5 5.2 5.6 9.3 10.8 10.8.9.3 1.9-.2 2.2-1.1l.9-2.8-3.3-1.9-1.6 1.6a12.4 12.4 0 0 1-4.3-4.3l1.6-1.6z" />
      </g>
    </svg>
  )
}

export function KeyboardIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      <path d="M7 10h.01M11 10h.01M15 10h.01M8.5 14h7" />
    </svg>
  )
}

export function CaptionsIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M10 10.5a2.2 2.2 0 1 0 0 3" />
      <path d="M17 10.5a2.2 2.2 0 1 0 0 3" />
    </svg>
  )
}

export function CheckIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4.5 12.5l5 5 10-11" />
    </svg>
  )
}

export function CloseIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M5.5 5.5l13 13M18.5 5.5l-13 13" />
    </svg>
  )
}

export function BackIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M14.5 5l-7 7 7 7" />
    </svg>
  )
}

export function SendIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 12h14" />
      <path d="M13 6.5l5.5 5.5-5.5 5.5" />
    </svg>
  )
}

export function GearIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.4M12 18.8v2.4M21.2 12h-2.4M5.2 12H2.8M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7M18.5 18.5l-1.7-1.7M7.2 7.2L5.5 5.5" />
    </svg>
  )
}

export function PersonIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
    </svg>
  )
}

export function EditIcon({ size = 22, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 20h4l10-10-4-4L4 16z" />
      <path d="M13.5 6.5l4 4" />
    </svg>
  )
}

/** The Blossom mark, static. Used in headers where the orb would be too loud. */
export function BlossomMark({ size = 22, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      aria-hidden
      focusable={false}
      className={className}
    >
      <path
        d="M 104 60 A 44 44 0 1 0 60 104"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle cx="60" cy="104" r="9" fill="var(--brand-sahel)" />
    </svg>
  )
}
